import React, { useEffect, useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Space, Spin, Typography } from 'antd';
import moment from 'moment';
import AnnouncementsTable from './components/AnnouncementsTable';
import AnnouncementSearch from './components/AnnouncementSearch';
import AnnouncementFormModal from './components/AnnouncementFormModal';
import RequestRetractModal from './components/RequestRetractModal';
import AnnouncementsHeader from './components/AnnouncementsHeader';
import useCourseOfferAnnouncementsData from '../../hooks/useCourseOfferAnnouncementsData';
import { announcementsHeaderStaticProps, trainerRoles } from './constants';
import { checkIsTrainer } from './utilities';
import { ERROR, REQUEST_RETRACT_SUCCESS } from '../../utilities/constants';

import './CourseOfferAnnouncementsContainer.scss';

const { Text } = Typography;
const courseOfferAnnouncementsContainerClassName =
  'c-CourseOfferAnnouncementsContainer';

const CourseOfferAnnouncementsContainer = () => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [offset, setOffset] = useState((current - 1) * pageSize);
  const [isAdmin, setIsAdmin] = useState(false);
  const [trainerId, setTrainerId] = useState(null);
  const [retractCourseOfferId, setRetractCourseOfferId] = useState(null);
  const [addModalOpened, setAddModelOpened] = useState(false);

  const onClickModal = () => {
    setAddModelOpened(true);
  };

  const {
    fetching,
    status,
    courseOffers,
    trainers,
    retryTrainersFetch,
    courseOfferAnnouncements,
    retryCourseOffersFetch,
    retryCoureOfferAnnouncementsFetch,
    createCourseOfferAnnouncement,
    createBid,
    deleteBid,
    courses,
    requestRetractFromCourseOffer,
    totalCount,
  } = useCourseOfferAnnouncementsData();

  const getCourseOffers = (courseId) => {
    retryCourseOffersFetch({ courseId });
  };

  const getTrainers = (courseOfferId) => {
    retryTrainersFetch(courseOfferId);
  };

  useEffect(() => {
    const authObject = window.localStorage.getItem('auth');
    const parsedAuth = JSON.parse(authObject);
    setTrainerId(parsedAuth?.user.trainerId);
    setIsAdmin(parsedAuth?.user?.isAdmin);
  }, []);

  useEffect(() => {
    retryCoureOfferAnnouncementsFetch(current, pageSize);
  }, [current, pageSize]);

  const getHandlerAndLabel = (hasBided) =>
    hasBided ? [deleteBid, 'Cancel Bid'] : [createBid, 'Bid'];

  const openRequestRetractModal = (courseOfferId) => {
    setRetractCourseOfferId(courseOfferId);
  };
  const closeRequestRetractModal = () => {
    setRetractCourseOfferId(null);
  };

  const onRetractRequest = (values) => {
    retractForm.resetFields();
    requestRetractFromCourseOffer(retractCourseOfferId, values);
    closeRequestRetractModal();
  };

  const renderBidButton = (
    isAssignedForRole,
    isAssignedForAnyRole,
    id,
    handler,
    label,
    role
  ) => {
    let disabled = false;
    if (isAssignedForRole) {
      return 'Assigned';
    } else if (isAssignedForAnyRole) {
      disabled = true;
    }

    return (
      <Button onClick={() => handler(id, role)} disabled={disabled}>
        {label}
      </Button>
    );
  };

  const columnsCollection = {
    noIndex: {
      title: 'No',
      dataIndex: 'index',
      key: 'index',
      render: (value, record, index) => {
        const label = <span>{offset + index + 1}</span>;
        return isAdmin ? (
          <Space size="middle">
            <Link to={'/course-offer-announcements/' + record._id}>
              {label}
            </Link>
          </Space>
        ) : (
          label
        );
      },
    },
    courseOfferCode: {
      title: 'Course Offer Code',
      dataIndex: 'courseOfferCode',
      key: 'courseOfferCode',
      sorter: (a, b) =>
        parseInt(a?.courseOfferId?.code) - parseInt(b?.courseOfferId?.code),
      render: (value, record) => {
        const label = record?.courseOfferId?.code;

        return isAdmin ? (
          <Space size="middle">
            <Link to={'/course-offer/' + record?.courseOfferId?.courseOfferId}>
              {label}
            </Link>
          </Space>
        ) : (
          label
        );
      },
    },
    course: {
      title: 'Course',
      dataIndex: 'course',
      key: 'course',
      sorter: (a, b) =>
        a?.courseOfferId?.courseDescription?.localeCompare(
          b?.courseOfferId?.courseDescription
        ),
      render: (value, record) => {
        const type = record?.courseOfferId?.type ? 'OD' : 'live';
        const label = `${
          record?.courseOfferId?.description.split('-')[0]
        } (${type})`;

        return isAdmin ? (
          <Space size="middle">
            <Link to={`/course/${record?.courseOfferId?.courseCode}`}>
              {label}
            </Link>
          </Space>
        ) : (
          label
        );
      },
    },
    isAssigned: {
      title: 'Is Assigned',
      dataIndex: 'assigned',
      key: 'assigned',
      sorter: (a, b) => {
        const aFormatted = a?.assigned ? 1 : 0;
        const bFormatted = b?.assigned ? 1 : 0;
        return aFormatted - bFormatted;
      },
      render: (value, record) => <span>{`${record.assigned}`}</span>,
    },
    leadTrainerBidsCount: {
      title: 'Lead Trainer Bids',
      dataIndex: 'leadTrainerBidsCount',
      sorter: (a, b) => a?.leadTrainerBidsCount - b?.leadTrainerBidsCount,
      key: 'leadTrainerBidsCount',
      render: (value, record) => <span>{record.leadTrainerBidsCount}</span>,
    },
    assistantTrainerBidsCount: {
      title: 'Assistant Trainer Bids',
      dataIndex: 'assistantTrainerBidsCount',
      key: 'assistantTrainerBidsCount',
      sorter: (a, b) =>
        a?.assistantTrainerBidsCount - b?.assistantTrainerBidsCount,
      render: (value, record) => (
        <span>{record.assistantTrainerBidsCount}</span>
      ),
    },
    supportTrainersBidsCount: {
      title: 'Support Staff',
      dataIndex: 'supportStaffBidsCount',
      sorter: (a, b) =>
        a?.supportTrainersBidsCount - b?.supportTrainersBidsCount,
      key: 'supportStaffBidsCount',
      render: (value, record) => <span>{record.supportTrainersBidsCount}</span>,
    },
    startDate: {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',

      sorter: (a, b) =>
        moment(a?.courseOfferId?.startDate).unix() -
        moment(b?.courseOfferId?.startDate).unix(),
      render: (value, record) => {
        const dateObject = new Date(record.courseOfferId.startDate);
        const dateString = dateObject.toLocaleDateString();

        return <span>{dateString}</span>;
      },
    },
    endDate: {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (a, b) =>
        moment(a?.courseOfferId?.endDate).unix() -
        moment(b?.courseOfferId?.endDate).unix(),
      render: (value, record) => {
        const dateObject = new Date(record.courseOfferId.endDate);
        const dateString = dateObject.toLocaleDateString();

        return <span>{dateString}</span>;
      },
    },
    startTime: {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (value, record) => {
        const dateObject = new Date(record.courseOfferId.startDate);
        const timeString = dateObject.toLocaleTimeString();

        return <span>{timeString}</span>;
      },
    },
    endTime: {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (value, record) => {
        const dateObject = new Date(record.courseOfferId.endDate);
        const timeString = dateObject.toLocaleTimeString();

        return <span>{timeString}</span>;
      },
    },
    frequency: {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (value, record) => {
        const frequency = record?.courseOfferId?.frequency
          ? record?.courseOfferId?.frequency?.map((text) => `${text} `)
          : ['Single Session'];
        return <span>{Children.toArray(frequency)}</span>;
      },
    },
    bidForLeadTrainer: {
      title: 'Lead Trainer Role',
      dataIndex: 'bidForLeadTrainer',
      key: 'bidForLeadTrainer',
      render: (value, record) => {
        const hasBided = record?.leadTrainer?.bids?.some(
          (trainer) => trainer === trainerId
        );

        const { isLeadTrainer, isTrainer } = checkIsTrainer(record, trainerId);

        return renderBidButton(
          isLeadTrainer,
          isTrainer,
          record._id,
          ...getHandlerAndLabel(hasBided),
          trainerRoles.LEAD_TRAINER
        );
      },
    },
    bidForAssistantTrainer: {
      title: 'Assistant Trainer Role',
      dataIndex: 'bidForAssistantTrainer',
      key: 'bidForAssistantTrainer',
      render: (value, record) => {
        const hasBided = record?.assistantTrainer?.bids?.some(
          (trainer) => trainer === trainerId
        );
        const { isAssistantTrainer, isTrainer } = checkIsTrainer(
          record,
          trainerId
        );

        return renderBidButton(
          isAssistantTrainer,
          isTrainer,
          record._id,
          ...getHandlerAndLabel(hasBided),
          trainerRoles.ASSISTANT_TRAINER
        );
      },
    },
    bidForSupportTrainers: {
      title: 'Support Trainer Role',
      dataIndex: 'bidForSupportTrainer',
      key: 'bidForSupportTrainer',
      render: (value, record) => {
        const hasBided = record?.supportTrainers?.bids?.some(
          (trainer) => trainer === trainerId
        );

        const { isSupportTrainer, isTrainer } = checkIsTrainer(
          record,
          trainerId
        );

        return renderBidButton(
          isSupportTrainer,
          isTrainer,
          record._id,
          ...getHandlerAndLabel(hasBided),
          trainerRoles.SUPPORT_TRAINERS
        );
      },
    },
    bidStatus: {
      title: 'Status',
      dataIndex: 'bidStatus',
      key: 'bidStatus',
      render: (value, record) => {
        const { leadTrainer, assistantTrainer, supportTrainers } = record;
        const assignedTrainers = [
          leadTrainer?.selected,
          assistantTrainer?.selected,
          ...supportTrainers?.selected,
        ];
        const trainerAssigned = assignedTrainers.some(
          (trainer) => trainer == trainerId
        );

        if (trainerAssigned)
          return (
            <Button onClick={() => openRequestRetractModal(record._id)}>
              Request Retract
            </Button>
          );

        return <span>Not Assigned</span>;
      },
    },
  };

  const adminColumns = [
    'noIndex',
    'courseOfferCode',
    'course',
    'isAssigned',
    'leadTrainerBidsCount',
    'assistantTrainerBidsCount',
    'supportTrainersBidsCount',
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'frequency',
  ];
  const trainerColumns = [
    'noIndex',
    'courseOfferCode',
    'course',
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'frequency',
    'bidForLeadTrainer',
    'bidForAssistantTrainer',
    'bidForSupportTrainers',
    'bidStatus',
  ];

  const renderColumns = () => {
    const columns = isAdmin ? adminColumns : trainerColumns;
    return columns.map((item) => columnsCollection[item]);
  };

  const onPaginationChange = (data) => {
    setCurrent(data.current);
    setPageSize(data.pageSize);
    setOffset(data.current * data.pageSize - data.pageSize);
  };

  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const [retractForm] = Form.useForm();

  const onReset = () => {
    form.resetFields();
    setOffset(0);
    setCurrent(1);
  };

  const onFinish = (values) => {
    const formatted = {
      assigned: values.isAssigned,
      courseOfferCode: values.courseOfferCode,
      startDate: values.startDate,
      endDate: values.endDate,
    };

    if (formatted.startDate)
      formatted.startDate = formatted.startDate.format('YYYY-MM-DD');
    if (formatted.endDate)
      formatted.endDate = formatted.endDate.format('YYYY-MM-DD');

    setCurrent(1);
    retryCoureOfferAnnouncementsFetch(1, pageSize, formatted);
  };

  const onCreateAnnouncement = (values) => {
    addForm.resetFields();
    setAddModelOpened(false);
    createCourseOfferAnnouncement(values);
  };

  if (fetching)
    return (
      <div
        className={`${courseOfferAnnouncementsContainerClassName}__text-center`}
      >
        <Spin />
      </div>
    );

  return (
    <div>
      <div>
        {status.type === ERROR && <Text type="danger">{status.message}</Text>}
        {status.type === REQUEST_RETRACT_SUCCESS && (
          <Text type="success">{status.message}</Text>
        )}
        <AnnouncementsHeader
          onClick={onClickModal}
          isAdmin={isAdmin}
          {...announcementsHeaderStaticProps}
        />

        <AnnouncementFormModal
          form={addForm}
          isOpen={addModalOpened}
          onClose={() => setAddModelOpened(!addModalOpened)}
          status={status}
          courses={courses}
          getCourseOffers={getCourseOffers}
          courseOffers={courseOffers}
          getTrainers={getTrainers}
          trainers={trainers}
          onSubmit={onCreateAnnouncement}
          title="Add Announcement"
          type="Add"
          fetching={fetching}
        />

        <section>
          <AnnouncementSearch
            onReset={onReset}
            onFinish={onFinish}
            form={form}
            initialValues={{ param: 'wave' }}
            courseOffers={courseOffers}
            getCourseOffers={getCourseOffers}
            courses={courses}
          />
        </section>
        <section>
          <AnnouncementsTable
            data={courseOfferAnnouncements}
            onPaginationChange={onPaginationChange}
            current={current}
            totalCount={totalCount}
            fetching={fetching}
            columns={renderColumns()}
            withHighlight={isAdmin}
          />
        </section>
      </div>
      <RequestRetractModal
        title="Request retract from role"
        isOpen={!!retractCourseOfferId}
        onCancel={closeRequestRetractModal}
        form={retractForm}
        handleSubmit={onRetractRequest}
      />
    </div>
  );
};

export default CourseOfferAnnouncementsContainer;
